import * as Yup from "yup";
import codes from "country-calling-code";
import { CreateClientDto } from "@containers/Contact/interfaces";
import { Account, RoleName, User } from "@shared/models";
import { FORM_ERROR_MESSAGES } from "@shared/constants";
import { emailValidator } from "@shared/validations";

export interface ClientFormShape {
  id: number | undefined;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  phone_country_code?: string;
  sms_on_lead_creation?: boolean;
  email_on_lead_creation?: boolean;
  roles?: RoleName[];
  additional_emails?: string[];
}

const createValidationSchema = () => {
  return Yup.object().shape({
    id: Yup.number().optional(),
    first_name: Yup.string()
      .max(35, `First name ${FORM_ERROR_MESSAGES.LONG}`)
      .trim()
      .required(`This ${FORM_ERROR_MESSAGES.REQUIRED}`),
    last_name: Yup.string()
      .max(35, `Last name ${FORM_ERROR_MESSAGES.LONG}`)
      .trim()
      .required(`This ${FORM_ERROR_MESSAGES.REQUIRED}`),
    email: emailValidator,
    additional_emails: Yup.array().of(emailValidator),
    email_on_lead_creation: Yup.boolean(),
    sms_on_lead_creation: Yup.boolean(),
    phone_country_code: Yup.string().optional(),
    phone_number: Yup.string()
      .optional()
      .when("sms_on_lead_creation", {
        is: true,
        then: Yup.string()
          .matches(/\(\d{3}\)\s\d{3}-\d{4}/, FORM_ERROR_MESSAGES.PHONE_NUMBER)
          .required(`This ${FORM_ERROR_MESSAGES.REQUIRED}`),
      }),
    roles: Yup.array()
      .of(Yup.mixed<RoleName>().oneOf(Object.values(RoleName)).required())
      .when("id", {
        is: (id: string) => !id, // If there is no 'id'
        then: Yup.array()
          .of(Yup.string())
          .min(1, "Role is required")
          .test(
            "admin-or-user",
            "At least one [User/Admin] role must be assigned during creation.",
            (roles) =>
              roles?.includes(RoleName.user) ||
              roles?.includes(RoleName.admin) ||
              roles?.includes(RoleName.super_admin) ||
              false,
          ),
        otherwise: Yup.array()
          .of(Yup.string())
          .test(
            "admin-or-user",
            "You cannot remove the required [User/Admin] role. At least one must remain assigned",
            (roles) =>
              roles?.includes(RoleName.user) ||
              roles?.includes(RoleName.admin) ||
              roles?.includes(RoleName.super_admin) ||
              false,
          ),
      }),
  });
};

export const validationSchema = createValidationSchema();

export const getInitValues = (account: Account, user: User | null) => {
  const accUser = user?.account_users?.find((i) => i.account_id === account.id);

  return {
    id: user?.id,
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    additional_emails: user?.additional_emails.map((item) => item.email) ?? [],
    roles: user?.roles.map((c) => c.name) || [],
    phone_number: user?.phone_number?.replace(/(\d\d\d)(\d\d\d)(\d\d\d\d)/, "($1) $2-$3") || "",
    phone_country_code: user?.phone_country_code || codes.find((c) => c.isoCode2 === "US")?.countryCodes?.[0] || "",
    sms_on_lead_creation: !!accUser?.sms_on_lead_creation,
    email_on_lead_creation: !!accUser?.email_on_lead_creation,
  };
};

export const prepareSubmitValue = (value: ClientFormShape, account: Account): Partial<CreateClientDto> => ({
  first_name: value.first_name,
  last_name: value.last_name,
  phone_number: value.phone_number?.replace(/[^\d]/g, ""),
  phone_country_code: value.phone_country_code,
  sms_on_lead_creation: !!value?.sms_on_lead_creation,
  email_on_lead_creation: !!value?.email_on_lead_creation,
  roles: value.roles,
  email: value.email,
  account_id: account.id,
  additional_emails: value.additional_emails,
});
